import {none, Option, some} from "ts-option";
import BaseFormController from "./BaseFormController";

const uuidv4 = require('uuid/v4');

type Rule<T> = (form: BaseFormController<any>, v: T) => string[]

/**
 * Base interface for form field.
 */
export interface FormField<T> {
	/**
	 * Validate field and return updated field (with errors).
	 * @return updated field.
	 */
	validate(form: BaseFormController<any>): FormField<T>

	/**
	 * Update value and return updated field.
	 * @param value new value.
	 * @return updated field.
	 */
	updateValue(value: Option<T>): FormField<T>

	/**
	 * Get first error.
	 * @return first error.
	 */
	firstError(): Option<string>

	/**
	 * Get value from field to create object generated by the form.
	 * @return value.
	 */
	getValue(): Option<T> | T

	/**
	 * Get optional value from field to create object generated by the form.
	 * @return {Option<T>}
	 */
	getOptionalValue(): Option<T>

	/**
	 * Value of the field.
	 */
	value: Option<T>

	/**
	 * Id of the field.
	 */
	id: string;

	/**
	 * Sets errors for field.
	 * @param {string[]} errors errors to set.
	 * @return {FormField<T>} updated field.
	 */
	setErrors(errors: string[]): FormField<T>

	/**
	 * Indicate if form field have value set.
	 * @return {boolean} true if any value is set.
	 */
	isDefined(): boolean

	/**
	 * Set rules for form field.
	 * @param {Rule<T>} rule rule to set.
	 * @return {FormField<T>} updated form field.
	 */
	setRules(rule: Rule<T>[]): FormField<T>;
}

/**
 * Single form field with mandatory value.
 */
export class RequiredFormField<T> implements FormField<T> {

	private constructor(public readonly id: string,
		public readonly value: Option<T>,
		public readonly errors: string[],
		public readonly rules: Rule<T>[],
		public readonly onMissingError: string) {
	}

	setRules(rules: Rule<T>[]): FormField<T> {
		return new RequiredFormField(this.id, this.value, this.errors, rules, this.onMissingError);
	}

	static createEmpty<T>(onMissingError: string = "Field is required", rules: Rule<T>[] = []): RequiredFormField<T> {
		const id = uuidv4();
		return new RequiredFormField(id, none, [], rules, onMissingError);
	}

	static create<T>(data: Option<T>,
		onMissingError: string = "Field is required",
		rules: Rule<T>[] = []): RequiredFormField<T> {
		const id = uuidv4();
		return new RequiredFormField(id, data, [], rules, onMissingError);
	}

	validate(form: BaseFormController<any>): RequiredFormField<T> {
		return this.value.map(v => {
			let errors: string[] = [];
			this.rules.forEach(rule => errors = errors.concat(rule(form, v)));
			return new RequiredFormField<T>(this.id, this.value, errors, this.rules, this.onMissingError)
		}).getOrElse(() => {
			return new RequiredFormField<T>(this.id, this.value, [this.onMissingError], this.rules, this.onMissingError)
		});
	}

	updateValue(value: Option<T>): RequiredFormField<T> {
		return new RequiredFormField<T>(this.id, value, this.errors, this.rules, this.onMissingError);
	}

	firstError(): Option<string> {
		if (this.errors.length > 0)
			return some(this.errors[0]);
		else
			return none;
	}

	getValue(): T {
		return this.value.get;
	}

	getOptionalValue(): Option<T> {
		return this.value;
	}

	setErrors(errors: string[]): RequiredFormField<T> {
		return new RequiredFormField<T>(this.id, this.value, errors, this.rules, this.onMissingError);
	}

	isDefined(): boolean {
		return this.value.isDefined
	}
}

/**
 * Single form field with optional value.
 */
export class OptionalFormField<T> implements FormField<T> {

	constructor(public readonly id: string,
		public readonly value: Option<T>,
		public readonly errors: string[],
		public readonly rules: Rule<T>[]) {}

	setRules(rules: Rule<T>[]): FormField<T> {
		return new OptionalFormField(this.id, this.value, this.errors, rules);
	}

	static createEmpty<T>(rules: Rule<T>[] = []): OptionalFormField<T> {
		const id = uuidv4();
		return new OptionalFormField(id, none, [], rules);
	}

	static create<T>(data: Option<T>, rules: Rule<T>[] = []): OptionalFormField<T> {
		const id = uuidv4();
		return new OptionalFormField(id, data, [], rules);
	}

	validate(form: BaseFormController<any>): OptionalFormField<T> {
		return this.value.map(v => {
			let errors: string[] = [];
			this.rules.forEach(rule => errors = errors.concat(rule(form, v)));
			return new OptionalFormField<T>(this.id, this.value, errors, this.rules)
		}).getOrElse(() => {
			let errors: string[] = [];
			this.rules.forEach(rule => errors = errors.concat(rule(form, this.value.orNull)));
			return new OptionalFormField<T>(this.id, this.value, errors, this.rules);
		});
	}

	updateValue(value: Option<T>): OptionalFormField<T> {
		return new OptionalFormField<T>(this.id, value, this.errors, this.rules);
	}

	firstError(): Option<string> {
		if (this.errors.length > 0)
			return some(this.errors[0]);
		else
			return none;
	}

	getValue(): Option<T> {
		return this.value;
	}

	getOptionalValue(): Option<T> {
		return this.value;
	}

	setErrors(errors: string[]): OptionalFormField<T> {
		return new OptionalFormField<T>(this.id, this.value, errors, this.rules)
	}

	isDefined(): boolean {
		return this.value.isDefined
	}
}